<template>
  <AppDialog
    v-model="dialog"
    title="Add whale to sighting"
    max-width="1100px"
    scrollable
    @close="handleClose"
  >
    <v-card-text>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row class="pt-4">
          <v-col cols="7">
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Whale Info
            </div>
            <v-divider class="divider pb-4" />
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="whale.whaleType"
                  name="whale_type"
                  label="Type*"
                  required
                  density="compact"
                  :rules="rules"
                  :items="Object.values(enums.whaleType)"
                />
              </v-col>
              <v-col cols="3">
                <v-combobox
                  v-model="whale.whaleId"
                  name="whale_id"
                  label="Id*"
                  required
                  density="compact"
                  :rules="rules"
                  :items="whaleIds"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="whale.whaleName"
                  name="whale_name"
                  label="Name"
                  density="compact"
                />
              </v-col>
              <v-col
                v-if="whale.groupMemberId && whale.groupMemberId.toUpperCase() === 'A'"
                cols="3"
              >
                <v-text-field
                  v-model="whale.groupSize"
                  name="whale_group_size"
                  label="Group Size"
                  type="number"
                  density="compact"
                  hide-spin-buttons
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="whale.groupNumber"
                  name="whale_group_number"
                  label="Group #*"
                  density="compact"
                  :rules="rules"
                  :items="groupNumberItems"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="whale.groupMemberId"
                  name="whale_group_member_id"
                  label="Member ID*"
                  density="compact"
                  :rules="rules"
                  :items="groupMemberIdItems"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="whale.scarSequence"
                  name="scar_sequence"
                  label="Scar Seq*"
                  density="compact"
                  :rules="rules"
                />
              </v-col>
              <v-col
                v-if="whale.groupMemberId && whale.groupMemberId.toUpperCase() === 'A'"
                cols="3"
              >
                <v-text-field
                  v-model="whale.totalBnf"
                  name="whale_total_bnf"
                  label="BNF total"
                  type="number"
                  density="compact"
                  hide-spin-buttons
                />
              </v-col>
            </v-row>
            <v-row v-if="isOrca">
              <v-col cols="3">
                <v-select
                  v-model="whale.ecotype"
                  name="whale_ecotype"
                  label="Ecotype"
                  density="compact"
                  :items="enums.ecotype"
                  :disabled="ecotypeLocked"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="whale.clan"
                  name="whale_clan"
                  label="Clan"
                  density="compact"
                  :items="clanItems"
                  :disabled="!clanEnabled"
                />
              </v-col>
              <v-col cols="3">
                <v-select
                  v-model="whale.pod"
                  name="whale_pod"
                  label="Pod"
                  density="compact"
                  :items="podItems"
                  :disabled="!whale.clan"
                />
              </v-col>
              <v-col cols="3">
                <v-text-field
                  v-model="whale.matriline"
                  name="whale_matriline"
                  label="Matriline"
                  density="compact"
                />
              </v-col>
            </v-row>

            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Behaviour
            </div>
            <v-divider class="divider pb-4" />
            <v-row>
              <v-col cols="3">
                <v-select
                  v-model="whale.is_escort"
                  name="whale_is_escort"
                  label="Is Escort?"
                  density="compact"
                  :items="[{ title: 'Yes', value: '1' }, { title: 'No', value: '0' }, { title: 'NA', value: 'NA' }]"
                  item-title="title"
                  item-value="value"
                />
              </v-col>
              <v-col cols="9">
                <v-select
                  v-model="whale.bhv"
                  name="whale_behaviours"
                  label="Bhv*"
                  required
                  density="compact"
                  :rules="rules"
                  :items="Object.values(enums.sightingBehaviour)"
                  multiple
                  :max="4"
                />
              </v-col>
            </v-row>
            <v-row v-if="Array.isArray(whale.bhv) && (whale.bhv.includes('ISI') || whale.bhv.includes('J') || whale.bhv.includes('JOIN'))">
              <v-col
                v-if="whale.bhv.includes('ISI')"
                cols="6"
              >
                <v-text-field
                  v-model="whale.isiSpecies"
                  name="whale_isi_species"
                  label="ISI Species"
                  density="compact"
                />
              </v-col>
              <v-col
                v-if="whale.bhv.includes('J') || whale.bhv.includes('JOIN')"
                cols="6"
              >
                <v-text-field
                  v-model="whale.joinTime"
                  name="whale_join_time"
                  label="Join Time"
                  density="compact"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="5">
            <div
              class="font-weight-bold pt-4 text-h6"
            >
              Features
            </div>
            <v-divider class="divider pb-4" />
            <v-row
              v-for="(feat, index) in whale.features"
              :key="index"
              align="center"
              dense
            >
              <v-col cols="5">
                <v-select
                  v-model="feat.featureName"
                  :name="`whale_feature_${index + 1}`"
                  :label="`Feature ${index + 1}`"
                  density="compact"
                  :items="Object.values(enums.sightingBodyFeature)"
                />
              </v-col>
              <v-col cols="5">
                <v-text-field
                  v-model="feat.photoNumber"
                  :name="`feature_photo_number_${index + 1}`"
                  :label="`Photo #`"
                  type="number"
                  density="compact"
                  hide-spin-buttons
                />
              </v-col>
              <v-col cols="2">
                <v-btn
                  v-if="whale.features.length > 1"
                  icon
                  size="x-small"
                  @click="removeFeature(index)"
                >
                  <v-icon>mdi-minus</v-icon>
                </v-btn>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12">
                <v-btn
                  size="small"
                  @click="addFeature"
                >
                  <v-icon>mdi-plus</v-icon>
                  Add Feature
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn @click="handleClose">
        <v-icon>mdi-close</v-icon>
        Cancel
      </v-btn>
      <v-spacer />
      <v-btn
        color="primary"
        :disabled="!valid || saving"
        @click="save"
      >
        <v-icon>mdi-check</v-icon>
        <span v-show="!saving"> Save </span>
        <pulse-loader :loading="saving" />
      </v-btn>
    </v-card-actions>
  </AppDialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import AppDialog from "@/components/AppDialog";
import PulseLoader from "vue-spinner/src/PulseLoader";
import enums from "@/helpers/enums";
import orcaTaxonomy from "@/helpers/orcaTaxonomy";
import "mapbox-gl/dist/mapbox-gl.css";

export default {
  components: {
    AppDialog,
    PulseLoader,
  },

  data() {
    return {
      selectedWhale: {},
      valid: true,
      rules: [(v) => !!v || "This field is required!"],
      hourRules: [
        (v) =>
            (v && v >= 0) ||
            "Hour should be between 0 (midnight) and 23 (11:00 PM).",
        (v) =>
            (v && v <= 23) ||
            "Hour should be between 0 (midnight) and 23 (11:00 PM).",
      ],
      minuteRules: [
        (v) => (v && v >= 0) || "Minute should be between 0 and 59.",
        (v) => (v && v <= 59) || "Minute should be between 0 and 59.",
      ],
      specOtherValue: "",
      incOtherValue: "",
      locOtherValue: "",
      editConfirmDialog: false,
      emailDialog: false,
      sameVessel: false,
    };
  },

  computed: {
    enums() {
      return enums;
    },
    groupNumberItems() {
      return Array.from({ length: 25 }, (_, i) => String(i + 1));
    },
    groupMemberIdItems() {
      return Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));
    },
    ...mapGetters({
      saving: "sighting/whale/saving",
      whale: "sighting/whale/whale",
      dialog: "sighting/whale/dialog",
      parentSighting: "sighting/details/sighting",
      whaleIds: "whales/whaleIds",
    }),
    parentSpecies() {
      return this.parentSighting && this.parentSighting.species;
    },
    isOrca() {
      return orcaTaxonomy.isOrca(this.parentSpecies);
    },
    ecotypeLocked() {
      return orcaTaxonomy.ecotypeIsLocked(this.parentSpecies);
    },
    clanEnabled() {
      return orcaTaxonomy.clanEnabled(this.parentSpecies, this.whale.ecotype);
    },
    clanItems() {
      return orcaTaxonomy.clanOptions(this.parentSpecies, this.whale.ecotype);
    },
    podItems() {
      return orcaTaxonomy.podOptions(this.whale.clan);
    },
  },
  watch: {
    whale() {
      this.selectedWhale = this.whale;
      if (!this.whale.features || this.whale.features.length === 0) {
        this.whale.features = [{ featureName: "", photoNumber: "" }];
      }
      this.applyForcedEcotype();
    },
    parentSpecies() {
      this.applyForcedEcotype();
    },
    "whale.ecotype"() {
      if (!this.clanEnabled) {
        this.whale.clan = "";
        this.whale.pod = "";
      }
    },
    "whale.clan"(clan) {
      if (!clan) {
        this.whale.pod = "";
      } else if (this.whale.pod && !this.podItems.includes(this.whale.pod)) {
        this.whale.pod = "";
      }
    },
    'whale.bhv'(val) {
      if (Array.isArray(val) && val.length > 4) {
        this.whale.bhv = val.slice(0, 4);
      }
    },
    hour() {
      if (this.hour < 0) {
        this.hour = 23;
      } else if (this.hour > 23) {
        this.hour = 0;
      }
    },
    minute() {
      if (this.minute < 0) {
        this.minute = 59;
      } else if (this.minute > 59) {
        this.minute = 0;
      }
    },
    specOtherValueGetter() {
      this.specOtherValue = this.specOtherValueGetter;
    },
    incOtherValueGetter() {
      this.incOtherValue = this.incOtherValueGetter;
    },
    locOtherValueGetter() {
      this.locOtherValue = this.locOtherValueGetter;
    },
  },
  created() {
    this.loadWhaleIds();
  },
  methods: {
    ...mapActions({
      update: "sighting/whale/update",
      create: "sighting/details/createWhale",
      close: "sighting/whale/close",
      addToSightingWhales: "sighting/details/addToSightingWhales",
      loadWhaleIds: "whales/loadWhaleIds",
    }),
    applyForcedEcotype() {
      if (!this.whale) {
        return;
      }
      const forced = orcaTaxonomy.forcedEcotype(this.parentSpecies);
      if (forced) {
        this.whale.ecotype = forced;
      }
    },
    addFeature() {
      this.whale.features.push({ featureName: "", photoNumber: "" });
    },
    removeFeature(index) {
      this.whale.features.splice(index, 1);
    },
    async save() {
      await this.$refs.form.validate();
      if (!this.valid) {
        return;
      }
      await this.addToSightingWhales(this.whale)
      await this.close()
    },
    handleClose() {
      this.$refs.form.resetValidation();
      this.close();
    },
  },
};
</script>

<style>

.map {
  height: 67vh;
  width: 40rem;
  margin: 5vh 0.7rem 0 0.7rem;
}

.map-buttons {
  background-color: white;
  padding: 0.5rem 1rem 0;
}

.map-container {
  position: relative !important;
  margin-top: -2.5rem;
  background-color: white;
}


.divider {
  border-top-width: 3px;
}

.bottom-sticky {
  position: sticky;
  bottom: 0;
  z-index: 998;
}

.arrived-at-date-picker {
  background-color: white;
}

.date-picker-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.time-input-row {
  display: flex;
  align-items: center;
  width: 100%;
}
</style>
